import { useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Images } from 'lucide-react';

import { PAGES } from '../../constants/pages';

import {
    useGetDeliveriesQuery,
    type GetDeliveriesQueryVariables,
    useDeliveriesUpdatedSubscription,
} from 'data/__generated__';

import { DataView } from 'components/DataView';
import {
    PageActions,
    PageActionsMenu,
    PageBreadcrumb,
    PageBreadcrumbSegment,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';

import { buildDeliveryFilters } from './constants/deliveryFilters';
import { DELIVERY_ACTIONS } from './constants/deliveryActions';
import { DELIVERY_VIEWS } from './constants/deliveryViews';

import { deliveriesUpdatedHandler } from './utils/deliverySubscriptionsHandlers';
import { formatDateAsAnniversary } from 'helpers/dateTimes';

const ACTION_GROUPS: PageActionGroup[] = [
    {
        items: [{ label: 'Contrôle de photos', icon: <Images />, to: '/deliveries-daily-photos' }],
    },
];

type DateRangeValue = { gte?: number; lte?: number };

// Le périmètre du premier rendu, aligné sur la valeur par défaut du filtre « En cours » : la
// `DataView` ne remonte son état qu'après le montage, un périmètre différent ici ferait une requête
// pour rien.
const INITIAL_VARIABLES: GetDeliveriesQueryVariables = { startDate: null, endDate: null, filters: { isCurrent: true } };

// La query `deliveries(startDate, endDate)` attend des jours au format YYYY-MM-DD.
function timestampToYmd(timestamp: number): string {
    return formatDateAsAnniversary({ dateTime: new Date(timestamp) });
}

export const Deliveries = () => {
    useDeliveriesUpdatedSubscription({
        onData: ({ data: { data } }) => {
            if (data) {
                deliveriesUpdatedHandler(data.deliveriesUpdated);
            }
        },
    });

    // Seuls le périmètre « En cours », le site et la plage de dates changent l'ensemble chargé : ils
    // deviennent des variables de query. Tous les autres filtres portent un `accessor` et sont
    // appliqués par la `DataView` sur les lignes déjà chargées.
    const [variables, setVariables] = useState<GetDeliveriesQueryVariables>(INITIAL_VARIABLES);

    const handleFiltersChange = useCallback((filters: Record<string, unknown>) => {
        const range = filters.deliveryDateRange as DateRangeValue | undefined;
        const siteIds = (filters.siteId as string[] | undefined) ?? [];
        const next: GetDeliveriesQueryVariables = {
            startDate: range?.gte ? timestampToYmd(range.gte) : null,
            endDate: range?.lte ? timestampToYmd(range.lte) : null,
            // Décocher « En cours » lève le périmètre : c'est un `false` explicite, pas une absence —
            // sans lui le serveur retomberait sur son périmètre par défaut.
            filters: { isCurrent: filters.isCurrent === true, ...(siteIds.length ? { siteIds } : {}) },
        };
        // Comparaison par valeur : l'objet est reconstruit à chaque notification, une identité neuve
        // relancerait la query en boucle.
        setVariables((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    }, []);

    const {
        data: deliveriesData,
        loading: deliveriesLoading,
        error: deliveriesError,
    } = useGetDeliveriesQuery({ fetchPolicy: 'cache-and-network', variables });

    const deliveries = useMemo(() => deliveriesData?.deliveries ?? [], [deliveriesData]);

    // Il n'existe pas d'endpoint d'options pour les tournées : on les déduit des livraisons chargées.
    // Les libellés en base sont sales (« R02 » et « R02 » avec espace final coexistent), d'où le `trim`.
    const roundOptions = useMemo(() => {
        const names = new Set<string>();
        deliveries.forEach(({ roundName }) => {
            const trimmed = roundName?.trim();
            if (trimmed) {
                names.add(trimmed);
            }
        });
        return Array.from(names)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ value: name, label: name }));
    }, [deliveries]);

    const filters = useMemo(() => buildDeliveryFilters(roundOptions), [roundOptions]);

    if (deliveriesError) {
        throw new Error('Une erreur est survenue lors de la récupération des livraisons');
    }

    return (
        <Container>
            <Helmet title={`${PAGES.deliveries.label} · TOTADMIN`} />
            <PageHeader>
                <PageBreadcrumb>
                    <PageBreadcrumbSegment label={PAGES.deliveries.label} isCurrent />
                </PageBreadcrumb>
                <PageActions>
                    <PageActionsMenu groups={ACTION_GROUPS} />
                </PageActions>
            </PageHeader>
            <Content>
                <DataView
                    data={deliveries}
                    getRowId={({ _id }) => _id}
                    isLoading={deliveriesLoading && !deliveriesData}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    search={{
                        placeholder: 'Rechercher par ID, site, organisation, label ou tournée…',
                        accessor: ({ _id, siteId, organizationName, siteName, label, roundName }) => [
                            _id,
                            siteId,
                            organizationName,
                            siteName,
                            label,
                            roundName,
                        ],
                    }}
                    views={DELIVERY_VIEWS}
                    actions={DELIVERY_ACTIONS}
                />
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
